import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Send, User, Mail, MessageSquare, CheckCircle2, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

export function ReviewSuccessPage() {
  const navigate = useNavigate();

  return (
    <div className="pt-24 lg:pt-32 bg-slate-50 min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-white rounded-[2.5rem] p-8 md:p-12 text-center shadow-2xl border border-slate-100"
      >
        <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
          <CheckCircle2 size={56} className="animate-bounce" />
        </div>
        
        <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight mb-4">
          Tak for din anmeldelse af os!
        </h1>
        
        <p className="text-slate-600 text-lg mb-8 leading-relaxed">
          Vi sætter utrolig stor pris på, at du tog dig tid til at dele din oplevelse med KC Detailer. Din feedback hjælper os med at opretholde vores høje kvalitet.
        </p>

        <button 
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-8 py-4 rounded-full font-bold transition-all shadow-lg shadow-blue-900/20 active:scale-95"
        >
          <ArrowLeft size={18} />
          Tilbage til forsiden
        </button>
      </motion.div>
    </div>
  );
}

export default function ReviewPage() {
  const navigate = useNavigate();
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [status, setStatus] = useState(null); // 'loading', 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    // Add the rating to the data payload
    data.rating = rating.toString();

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus(null);
        e.target.reset();
        navigate('/anmeld/tak');
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <div className="pt-24 lg:pt-32 bg-slate-50 min-h-screen">
      {/* Hero Header */}
      <div className="bg-slate-900 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#33c3f7_1px,transparent_1px)] [background-size:20px_20px]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight mb-6"
          >
            Skriv en <span className="text-sky-400">Anmeldelse</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto"
          >
            Din feedback er guld værd. Fortæl os og andre om din oplevelse med KC Detailer, så vi kan blive ved med at levere det bedste resultat.
          </motion.p>
        </div>
      </div>

      {/* Form Area */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 pb-24 relative z-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-[2.5rem] p-8 md:p-12 text-slate-900 shadow-2xl border border-slate-100"
        >
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Honeypot */}
            <div className="sr-only absolute" aria-hidden="true">
              <label htmlFor="website_url">Website URL</label>
              <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
            </div>

            {/* Name and Email */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1 flex items-center gap-2">
                  <User size={16} className="text-slate-400" />
                  Navn
                </label>
                <input 
                  required 
                  name="name"
                  type="text" 
                  placeholder="Dit fulde navn" 
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1 flex items-center gap-2">
                  <Mail size={16} className="text-slate-400" />
                  E-mail
                </label>
                <input 
                  required 
                  name="email"
                  type="email" 
                  placeholder="din@mail.dk" 
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition-all"
                />
              </div>
            </div>

            {/* Star Rating */}
            <div className="space-y-3 bg-slate-50 p-6 rounded-2xl border border-slate-100">
              <label className="text-sm font-bold text-slate-700 ml-1 block text-center md:text-left">
                Antal stjerner
              </label>
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => {
                    const isFilled = hoverRating ? star <= hoverRating : star <= rating;
                    return (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-1 hover:scale-110 transition-transform focus:outline-none"
                      >
                        <Star 
                          size={36} 
                          className={`transition-colors duration-150 ${
                            isFilled 
                              ? 'fill-amber-400 text-amber-400' 
                              : 'text-slate-300'
                          }`} 
                        />
                      </button>
                    );
                  })}
                </div>
                <span className="text-sm font-bold text-slate-500 bg-white px-4 py-1.5 rounded-full shadow-sm border border-slate-100">
                  {rating === 5 && 'Fremragende! (5 ud af 5)'}
                  {rating === 4 && 'Rigtig godt! (4 ud af 5)'}
                  {rating === 3 && 'Godt (3 ud af 5)'}
                  {rating === 2 && 'Godkendt (2 ud af 5)'}
                  {rating === 1 && 'Dårligt (1 ud af 5)'}
                </span>
              </div>
            </div>

            {/* Longtext Field */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1 flex items-center gap-2">
                <MessageSquare size={16} className="text-slate-400" />
                Din anmeldelse
              </label>
              <textarea 
                required
                name="review_text"
                rows="6" 
                placeholder="Skriv din anmeldelse her... Fortæl os gerne om hvad vi gjorde godt, og hvad vi kan gøre endnu bedre." 
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition-all resize-none"
              ></textarea>
            </div>

            {/* Submit Button */}
            <button 
              disabled={status === 'loading'}
              type="submit" 
              className="w-full bg-blue-900 hover:bg-blue-800 text-white font-black py-5 rounded-full text-lg shadow-xl shadow-blue-900/10 transition-all flex items-center justify-center gap-3 active:scale-[0.98] disabled:opacity-70 cursor-pointer"
            >
              {status === 'loading' ? 'Indsender anmeldelse...' : 'Indsend anmeldelse'}
              <Send size={20} />
            </button>
            
            {status === 'error' && (
              <p className="text-red-600 text-center font-bold text-sm bg-red-50 py-3 rounded-xl border border-red-100">
                Der skete en fejl under indsendelsen. Prøv venligst igen eller kontakt os direkte.
              </p>
            )}
          </form>
        </motion.div>
      </div>
    </div>
  );
}
